import { sanitizeCurrentUrl, sanitizeReferrer } from './urlSanitizer';

export const GOOGLE_ANALYTICS_MEASUREMENT_ID = 'G-RZTLWL2LYY';
const GOOGLE_ANALYTICS_SCRIPT_ID = 'google-analytics-script';
const SAFE_PAGE_TITLE = 'Dawntech website';
const WEBSITE_COOKIE_DOMAIN = 'dawntech.dev';

let enabled = false;
let lastTrackedPageview;

const getSanitizedPathname = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const sanitizedUrl = sanitizeCurrentUrl();
  return new URL(sanitizedUrl || '/', window.location.origin).pathname;
};

const getGtag = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };

  return window.gtag;
};

export const loadGoogleAnalytics = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return false;
  }

  if (enabled) {
    return true;
  }

  try {
    window[`ga-disable-${GOOGLE_ANALYTICS_MEASUREMENT_ID}`] = false;
    const gtag = getGtag();
    gtag('js', new Date());
    gtag('config', GOOGLE_ANALYTICS_MEASUREMENT_ID, {
      page_location: sanitizeCurrentUrl(),
      page_referrer: sanitizeReferrer(),
      page_title: SAFE_PAGE_TITLE,
      send_page_view: false,
    });

    if (!document.getElementById(GOOGLE_ANALYTICS_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = GOOGLE_ANALYTICS_SCRIPT_ID;
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_MEASUREMENT_ID}`;
      document.head.appendChild(script);
    }

    enabled = true;
    trackGoogleAnalyticsPageView();
    return true;
  } catch (error) {
    return false;
  }
};

export const trackGoogleAnalyticsPageView = () => {
  if (!enabled) {
    return;
  }

  try {
    const pageLocation = sanitizeCurrentUrl();

    if (pageLocation === lastTrackedPageview) {
      return;
    }

    lastTrackedPageview = pageLocation;
    getGtag()('event', 'page_view', {
      page_location: pageLocation,
      page_path: getSanitizedPathname(),
      page_title: SAFE_PAGE_TITLE,
      page_referrer: sanitizeReferrer(),
    });
  } catch (error) {
    // Analytics is intentionally best effort.
  }
};

export const getGoogleAnalyticsCookieDomains = (hostname = '') => {
  if (!hostname) {
    return [];
  }

  const domains = [hostname];
  if (
    hostname !== WEBSITE_COOKIE_DOMAIN &&
    hostname.endsWith(`.${WEBSITE_COOKIE_DOMAIN}`)
  ) {
    domains.push(WEBSITE_COOKIE_DOMAIN);
  }

  return domains;
};

const clearGoogleAnalyticsCookies = () => {
  if (typeof document === 'undefined') {
    return;
  }

  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';

  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim();

    if (!name.startsWith('_ga')) {
      return;
    }

    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;

    getGoogleAnalyticsCookieDomains(hostname).forEach((domain) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${domain}`;
    });
  });
};

export const disableGoogleAnalytics = () => {
  enabled = false;
  lastTrackedPageview = undefined;

  if (typeof window !== 'undefined') {
    window[`ga-disable-${GOOGLE_ANALYTICS_MEASUREMENT_ID}`] = true;
    window.dataLayer = [];
    try {
      delete window.gtag;
    } catch (error) {
      window.gtag = undefined;
    }
  }

  if (typeof document !== 'undefined') {
    document.getElementById(GOOGLE_ANALYTICS_SCRIPT_ID)?.remove();
  }

  clearGoogleAnalyticsCookies();
};

export const isGoogleAnalyticsEnabled = () => enabled;
